import { IGif } from '@giphy/js-types'
import React, { useContext, useEffect } from 'react'
import { useChannelGifPaginator } from 'shared/api/channels'
import BrandLoader from 'shared/components/brand-loader'
import Observer from 'shared/components/observer'
import VideoPreviewPlayInline from 'shared/components/video-inline-play'
import styled from 'styled-components'
import { GridOverlayContext } from './grid-overlay'
import { useSameObjectRef } from 'shared/hooks/use-same-ref'
import BulkEditModeContext from './context/bulk-edit-mode'
import { useKeyPress } from 'react-use'
import BulkEditor from 'shared/components/bulk-editor'
import { useBulkEditGifClick } from 'shared/hooks/use-bulk-edit'

const StyledVideoPreview = styled(VideoPreviewPlayInline)`
    margin-bottom: 30px;
`

export const Grid = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 30px;
    justify-content: space-between;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
`

export const Loader = styled(BrandLoader)`
    margin-bottom: 50px;
`

// spaces grid to align items to the left
const PreviewSpacer = styled.div<{ previewWidth: number }>`
    height: 1px;
    width: ${props => props.previewWidth}px;
`

type Props = {
    gifs?: IGif[]
    previewWidth?: number
    hideAttribution?: boolean
}

type GifId = string | number

const VideoPreviewGrid = ({ gifs = [], previewWidth = 336, hideAttribution = false }: Props) => {
    const standaloneSelectionMode = false
    const { isBulkEditMode, setBulkEditGifIds: setSelectedGifs, bulkEditGifIds: selectedGifs } = useContext(
        BulkEditModeContext
    )
    const iSelectionMode = !!(isBulkEditMode || standaloneSelectionMode)
    const [isEscPressed] = useKeyPress((event: KeyboardEvent) => event.keyCode === 27)
    const gifIds: GifId[] = gifs.map(g => g.id)

    const onGifClick = useBulkEditGifClick({ gifs: gifIds, iSelectionMode, standaloneSelectionMode })

    useEffect(() => {
        setSelectedGifs([])
    }, [isEscPressed, setSelectedGifs])

    useEffect(() => {
        return () => setSelectedGifs([])
    }, [setSelectedGifs])

    const gridOverlayValue = useSameObjectRef({
        onGifClick,
        selectedGifs,
        iSelectionMode,
    })

    return (
        <GridOverlayContext.Provider value={gridOverlayValue}>
            <Grid>
                {gifs.map((gif, key) => (
                    <StyledVideoPreview
                        key={gif.id}
                        gif={gif}
                        width={previewWidth}
                        gridPosition={key + 1}
                        showClipTitle={true}
                        showAttribution={!hideAttribution}
                    />
                ))}
                <PreviewSpacer previewWidth={previewWidth} />
            </Grid>
        </GridOverlayContext.Provider>
    )
}

type ChannelGridProps = { channelId: number } & Props

export const VideoPreviewChannelGrid = ({ channelId, previewWidth, hideAttribution = false }: ChannelGridProps) => {
    const [gifs = [], fetchVideos, isDoneLoading] = useChannelGifPaginator(channelId, 'videos')
    return (
        <>
            <VideoPreviewGrid gifs={gifs} previewWidth={previewWidth} hideAttribution={hideAttribution} />
            {!isDoneLoading && (
                <Observer onVisible={fetchVideos} key={gifs.length}>
                    <Loader />
                </Observer>
            )}
            <BulkEditor />
        </>
    )
}

export default VideoPreviewGrid
